/**
 * V2 router — the URL is the single source of truth for what's on screen.
 *
 * Differences from the V1 router: the base path is configuration (not a
 * hardcoded constant), routes are a declarative table, and navigation has
 * exactly one entry point (navigate). There is no separate "current view"
 * state to keep in sync.
 */

/**
 * Route table. Patterns are path segments; ':name' captures a param.
 * Order matters: first match wins.
 */
const ROUTE_TABLE = [
  { name: 'discover', pattern: [] },
  { name: 'log', pattern: ['log', ':locationId'] },
  { name: 'log', pattern: ['log'] },
  { name: 'account', pattern: ['account'] },
  { name: 'admin', pattern: ['admin', ':tab'] },
  { name: 'admin', pattern: ['admin'] },
  { name: 'lists-import', pattern: ['lists', 'import'] },
  { name: 'featured-lists', pattern: ['lists', 'featured'] },
  { name: 'import-reviews', pattern: ['import', 'reviews'] },
  { name: 'import', pattern: ['import'] },
  { name: 'lists', pattern: ['lists'] },
  { name: 'save', pattern: ['save'] },
  { name: 'phone', pattern: ['phone'] },
  { name: 'route', pattern: ['route'] },
  { name: 'categories', pattern: ['categories'] },
  { name: 'medals', pattern: ['medals'] },
  { name: 'stamp', pattern: ['category', ':slug', 'stamp'] },
  { name: 'category', pattern: ['category', ':slug'] },
  { name: 'location', pattern: ['location', ':id'] },
  { name: 'list', pattern: ['user', ':username', 'list', ':slug'] },
  { name: 'personal-category', pattern: ['user', ':username', 'category', ':slug'] },
  { name: 'user-geography', pattern: ['user', ':username', 'map'] },
  { name: 'user-trophies', pattern: ['user', ':username', 'trophies'] },
  { name: 'user-awards', pattern: ['user', ':username', 'awards'] },
  { name: 'user-collections', pattern: ['user', ':username', 'collections'] },
  { name: 'user-categories', pattern: ['user', ':username', 'categories'] },
  { name: 'user-lists', pattern: ['user', ':username', 'lists'] },
  { name: 'user', pattern: ['user', ':username'] },
  { name: 'yoink-edit', pattern: ['yoink', ':id', 'edit'] },
  { name: 'yoink', pattern: ['yoink', ':id'] },
  { name: 'glossary', pattern: ['glossary'] },
];

function matchRoute(segments) {
  for (const route of ROUTE_TABLE) {
    if (route.pattern.length !== segments.length) continue;
    const params = {};
    let ok = true;
    for (let i = 0; i < route.pattern.length; i++) {
      const pat = route.pattern[i];
      if (pat.startsWith(':')) {
        params[pat.slice(1)] = decodeURIComponent(segments[i]);
      } else if (pat !== segments[i]) {
        ok = false;
        break;
      }
    }
    if (ok) return { name: route.name, params };
  }
  // A real not-found, not a silent fall-through to Discover: rendering
  // the home feed at 200 for garbage URLs teaches crawlers that every
  // typo is a duplicate of the homepage.
  return { name: 'not-found', params: {} };
}

export function createRouter({ basePath = '/adventure', onChange }) {
  function parse(pathname = window.location.pathname) {
    let path = pathname;
    if (path.startsWith(basePath)) path = path.slice(basePath.length);
    const segments = path.split('/').filter(Boolean);
    return matchRoute(segments);
  }

  function generate(name, params = {}, query = null) {
    const route = ROUTE_TABLE.find((r) => r.name === name);
    if (!route) return `${basePath}/`;
    const segments = route.pattern.map((pat) =>
      pat.startsWith(':') ? encodeURIComponent(params[pat.slice(1)] ?? '') : pat
    );
    const path = segments.length ? `${basePath}/${segments.join('/')}` : `${basePath}/`;
    const qs = query ? String(new URLSearchParams(query)) : '';
    return qs ? `${path}?${qs}` : path;
  }

  function navigate(name, params = {}, { replace = false, query = null } = {}) {
    const url = generate(name, params, query);
    const stateData = { name, params };
    if (replace) {
      window.history.replaceState(stateData, '', url);
    } else {
      window.history.pushState(stateData, '', url);
    }
    onChange(stateData);
  }

  function init() {
    window.addEventListener('popstate', () => onChange(parse()));
    const current = parse();
    // Preserve query + hash when normalizing the initial URL: the OAuth
    // redirect carries the session in the hash (#access_token=…) and the
    // on-device diagnostics flag rides the query (?authdiag=1) — stripping
    // them here would break login and iPad debugging.
    const extras = window.location.search + window.location.hash;
    window.history.replaceState(current, '', generate(current.name, current.params) + extras);
    onChange(current);
  }

  return { parse, generate, navigate, init };
}
